"use client";

import { Suspense, useSyncExternalStore, type ReactNode } from "react";
import { TracewayProvider } from "@tracewayapp/react";
import TracewayActivityTracker from "./TracewayActivityTracker";

const subscribeAfterHydration = (onStoreChange: () => void) => {
  const timeoutId = window.setTimeout(onStoreChange, 0);
  return () => window.clearTimeout(timeoutId);
};

const getClientHydrationSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

type TracewayRootProviderProps = {
  connectionString?: string;
  appVersion?: string;
  env?: string;
  children: ReactNode;
};

export default function TracewayRootProvider({
  connectionString,
  appVersion,
  env,
  children,
}: TracewayRootProviderProps) {
  const trimmedConnectionString = connectionString?.trim();
  const isHydrated = useSyncExternalStore(
    subscribeAfterHydration,
    getClientHydrationSnapshot,
    getServerHydrationSnapshot,
  );

  // Keep the page in the same child slot on the server and every client render.
  // Switching between a sole children fragment and [children, diagnostics]
  // remounted the entire page as soon as diagnostics started.
  return (
    <>
      {children}
      {trimmedConnectionString && isHydrated ? <TracewayProvider
        connectionString={trimmedConnectionString}
        options={{
          version: appVersion,
          debug: env !== "production",
          recordAllSessions: true,
          captureHttpServerErrors: true,
        }}
      >
        <Suspense fallback={null}>
          <TracewayActivityTracker />
        </Suspense>
      </TracewayProvider> : null}
    </>
  );
}
